import React, { useEffect, useState } from 'react';
import Label from './parts/Label';
import FieldError from './parts/FieldError';
import Description from './parts/Description';
import i18n from '../measure-i18n';
import { Spacer } from '../../../helpers';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { colors, mixins } from '../../../core/styles/theme/default';

const StyledLabel = styled.p`
  font-weight: 600;
  font-size: ${rem(14)};
  margin: 0;
`;

const StyledOptionsWrapper = styled.div`
  display: grid;
  margin: ${rem(8)} 0 0;

  ${mixins.responsiveStyles({
    'grid-template-columns': {
      zero: '1fr',
      medium: 'repeat(4, 1fr)',
    },
    'grid-gap': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(24)} ${rem(32)}`,
    },
  })}
`;

const StyledCheckbox = styled.label`
  display: inline-flex;
  align-items: flex-start;
  font-size: ${rem(14)};
  gap: ${rem(8)};

  input {
    cursor: pointer;
    appearance: none;
    position: relative;
    min-height: 0;
    padding: 0;
    margin: ${rem(-2)} 0 0;
    height: ${rem(24)};
    width: ${rem(24)};
    flex-shrink: 0;

    &:checked:after {
      content: '✔';
      position: absolute;
      top: ${rem(2)};
      left: ${rem(3)};
      font-size: ${rem(18)};
      color: ${colors.grue800};
    }
  }
`;

const Matrix = ({
  id,
  label,
  prefix,
  groups,
  options,
  onChange,
  isRequired,
  error,
  errorRenderer,
  locale,
}: MatrixType) => {
  const [matrixData, setMatrixData] = useState<MatrixDataType>(() => {
    const obj: MatrixDataType = {};

    groups.forEach((group) => {
      obj[group.id] = {};

      group.fields.forEach((field) => {
        obj[group.id][field.id] = {};

        options.forEach((option) => {
          obj[group.id][field.id][option.id] = false;
        });
      });
    });

    return obj;
  });

  useEffect(() => {
    onChange(matrixData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixData]);

  return (
    <div>
      {label ? (
        <Label id={id} isRequired={isRequired}>
          {label}
        </Label>
      ) : null}
      {prefix ? <Description top>{prefix}</Description> : null}
      {error ? (
        errorRenderer ? (
          errorRenderer(error)
        ) : (
          <FieldError text={i18n.requiredAtLeastOne[locale]} />
        )
      ) : null}
      {groups.map((group) => (
        <div key={group.id}>
          <Spacer space={'gamma'} />
          <StyledLabel>{group.label}</StyledLabel>
          {group.fields.map((field) => (
            <div key={field.id}>
              <Spacer space={'alpha'} />
              <Description>{field.label}</Description>
              <StyledOptionsWrapper>
                {options.map((option) => (
                  <div key={option.id}>
                    <StyledCheckbox>
                      <input
                        type="checkbox"
                        name={`${group.id}.${field.id}.${option.id}`}
                        checked={
                          matrixData[group.id]?.[field.id]?.[option.id] === true
                        }
                        onChange={(e) => {
                          setMatrixData((prev) => ({
                            ...prev,
                            [group.id]: {
                              ...prev[group.id],
                              [field.id]: {
                                ...prev[group.id][field.id],
                                [option.id]: e.target.checked,
                              },
                            },
                          }));
                        }}
                      />
                      {option.label}
                    </StyledCheckbox>
                  </div>
                ))}
              </StyledOptionsWrapper>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const isValid = (value: MatrixDataType, config: any) => {
  if (!config.isRequired) return true;
  if (!value) return false;

  return Object.values(value).some((group) =>
    Object.values(group).some((field) =>
      Object.values(field).some((option) => option === true),
    ),
  );
};

type MatrixDataType = Record<string, Record<string, Record<string, boolean>>>;

export type MatrixType = {
  id: string;
  label: string;
  locale: string;
  prefix: string;
  isRequired: boolean;
  groups: {
    id: string;
    label: string;
    fields: {
      id: string;
      label: string;
    }[];
  }[];
  options: {
    id: string;
    label: string;
  }[];
  value?: MatrixDataType;
  onChange: (value?: MatrixType['value']) => void;
  error: string;
  errorRenderer: (error: string) => JSX.Element;
};

export default Matrix;
